import * as d3drag from 'd3-drag'
import * as d3 from 'd3-selection'
import _ from 'lodash'
import Magellan from 'magellan'

const Dnd = function (porter, options = {}) {
	let { dndContainerNodes } = porter

	//drag threshold time in ms before we start to register a drag, default is
	//0 for backwards compatibility
	this.dragThreshold = options.dragThreshold || 0

	let packageNodes = _(dndContainerNodes)
		.map(containerNode => {
			let nodes = Array.from(containerNode.getElementsByClassName('porter-package'))
			//the package itself can be the container as well as the destination
			if (containerNode.classList.contains('porter-package')) {
				nodes = [containerNode].concat(nodes)
			}
			return nodes
		})
		.flatten()
		.value()

	d3.selectAll(dndContainerNodes)
		.classed('is-empty', function () {
			return this.getElementsByClassName('porter-package').length === 0
		})

	let drag = d3drag.drag()
	drag.clickDistance(this.dragThreshold)

	let self = this
	drag.on('start', function(e) {
		// SpiderStrategies/Scoreboard#45337 don't start the drag if the
		// porter-package (this) is no-drag
		// SpiderStrategies/Scoreboard#47042 don't start the drag if the
		// node taht was clicked on (sourceEvent.target) is no-drag
		if (noDrag(this) || noDrag(e.sourceEvent.target)) return
		delete self._dragging

		//set relative positioning, package-to-mouse
		//needs to be done here because setTimeout kills d3 event
		let eventCoords = getEventCoords(e)
		let { top, left } = this.getBoundingClientRect()
		self.deliveryPosition = {
			offsetX: eventCoords.x - left,
			offsetY: eventCoords.y - top,
			originX: left,
			originY: top
		}

		if (self.dragThreshold > 0) {
			self.dragTimeout = setTimeout(self.initializeDrag.bind(self),
				self.dragThreshold, this, porter)
		} else {
			self.initializeDrag(this, porter)
		}
	})
	drag.on('drag', function(e) {
		if (!self._dragging) { return }
		self.onDrag(this, e, porter)
	})
	drag.on('end', function(e) {
		clearTimeout(self.dragTimeout)
		if (!self._dragging) { return }
		self.onDragEnd(this, e, porter)
	})

	d3.selectAll(packageNodes).call(drag)

	return this
}

Dnd.Delivery = {
	MOVE: 'move',
	COPY: 'copy',
	REJECT: 'reject'
}

function exitClosestDestination(porter) {
	if (porter.closestDestination) {
		/**
		 * exit event, emitted when the drag leaves a destination
		 *
		 * @event Porter#exit-destination
		 * @property {DOMNode} selectedDestination - the DOMNode destination the drag is leaving
		 */
		porter.emit('exit-destination', porter.closestDestination)
		delete porter.closestDestination
	}
}

Dnd.prototype.initializeDrag = function(node, porter) {
	this._dragging = true
	this.onDragStart(node, porter)
}

Dnd.prototype.onDragStart = function(node, porter) {
	let sourcePackage = node

	// Clone the package we're moving; this is what the user will drag around the
	// screen, while the original node will be left in place
	let delivery = sourcePackage.cloneNode(true)

	// Keep track of where the package came from
	let sourceDestination = sourcePackage
	if (!sourcePackage.classList.contains('porter-destination')) {
		sourceDestination = sourcePackage.parentElement
		while (!d3.select(sourceDestination).classed('porter-destination')) {
			sourceDestination = sourceDestination.parentElement
		}
	}
	d3.select(sourcePackage).classed('is-moving', true)

	// Position new cloned node on top of the original one, attached directly
	// to the document body
	d3.select(delivery)
		.classed('porter-delivery', true)
		.style('left', px(this.deliveryPosition.originX))
		.style('top', px(this.deliveryPosition.originY))
		.style('position', 'absolute')

	document.body.appendChild(delivery)

	sourcePackage.delivery = delivery
	sourcePackage.sourceDestination = sourceDestination

	d3.select(sourceDestination).classed('is-current-destination', true)

	/**
	 * Start event, emitted when the drag is first initiated
	 *
	 * @event Porter#start
	 * @property {DOMNode} sourcePackage - The DOMNode of the package being dragged
	 * @property {DOMNode} delivery - The drag "avatar" of the package (the node
	 * that moves along with the dragging of the node)
	 */
	porter.emit('start', { sourcePackage, delivery })
}

Dnd.prototype.onDrag = function(node, e, porter) {
	let sourcePackage = node
	let delivery = sourcePackage.delivery
	let { dndContainerNodes } = porter
	let eventCoords = getEventCoords(e)

	// Move the delivery by the same amount that the mouse moved
	d3.select(delivery)
		.style('left', px(eventCoords.x - this.deliveryPosition.offsetX))
		.style('top', px(eventCoords.y - this.deliveryPosition.offsetY))

	// Add a class to the destination that the delivery would be dropped into
	// on drag end
	let selectedDestination = currentDestination(dndContainerNodes, delivery, e)
	d3.selectAll(dndContainerNodes).classed('is-current-destination', function () {
		return this === selectedDestination
	})

	//emit notifications when enter/moving through/leaving destinations
	if (selectedDestination) {
		if (selectedDestination !== sourcePackage) {
			//determine where the drag in within the destination, split into
			//four quadrants: top-left, top-right, bottom-left, bottom-right
			let deliveryLocation = delivery.getBoundingClientRect()
			let targetRect = selectedDestination.getBoundingClientRect()
				, targetMiddleHeight = targetRect.y + targetRect.height / 2
				, targetMiddleWidth = targetRect.x + targetRect.width / 2
				, left = deliveryLocation.x < targetMiddleWidth ? true : false
				, top = deliveryLocation.y < targetMiddleHeight ? true : false

			let position = {left, top}
			if (porter.closestDestination !== selectedDestination) {
				//if the destintation changed, exit the previous one
				exitClosestDestination(porter)

				//and notify of the new one
				porter.closestDestination = selectedDestination

				/**
				 * enter event, emitted when the drag is first initiated
				 *
				 * @event Porter#enter-destination
				 * @property {DOMNode} selectedDestination - the DOMNode destination the drag entered
				 * @property {Boolean} position.top - True if the avatar's position is the top half of the destination's bound
				 * @property {Boolean} position.left - True if the avatar's position is the left half of the destination's bound
				 */
				porter.emit('enter-destination', selectedDestination, position)
			} else {
				/**
				 * over event, emitted after a destination was entered. used to notify listeners of drag positional changes
				 * while over the destination
				 *
				 * @event Porter#over-destination
				 * @property {DOMNode} selectedDestination - the DOMNode destination the drag is over
				 * @property {Boolean} position.top - True if the avatar's position is the top half of the destination's bound
				 * @property {Boolean} position.left - True if the avatar's position is the left half of the destination's bound
				 */
				porter.emit('over-destination', porter.closestDestination, position)
			}
		}
	} else {
		//we left the container, check to see if we were in a destination, if so
		//emit an exit event
		exitClosestDestination(porter)
	}
	porter.emit('drag', selectedDestination)
}

Dnd.prototype.onDragEnd = function(node, e, porter) {
	let sourcePackage = node
	let sourceDestination = sourcePackage.sourceDestination
	let delivery = sourcePackage.delivery
	let { dndContainerNodes, options } = porter

	d3.select(sourcePackage).classed('is-moving', false)
	d3.selectAll(dndContainerNodes).classed('is-current-destination', false)

	let destination = currentDestination(dndContainerNodes, delivery, e)

	delivery.parentNode.removeChild(delivery)
	delete sourcePackage.delivery
	delete sourcePackage.sourceDestination
	delete this.deliveryPosition

	exitClosestDestination(porter)

	let result = null
	// If dragging ending on a destination and it's not the same destination
	// that it came from, use accept() callback to determine what should happen
	if (destination && destination.dataset.id !== sourceDestination.dataset.id) {
		let args = {
			sourcePackage: {
				id: sourcePackage.dataset.id,
				node: sourcePackage
			},
			destination: {
				id: destination.dataset.id,
				node: destination
			},
			source: {
				id: sourceDestination.dataset.id,
				node: sourceDestination
			}
		}

		// options.accept() allows the calling code to decide exactly how
		// the delivery should be handled
		result = options.accept(args)

		if (result == Dnd.Delivery.MOVE) {
			if (porter.options.appendOnAcceptance) {
				// Move the package DOM node to the new destination
				destination.appendChild(sourcePackage)
			}

			args.deliveredPackage = {
				id: sourcePackage.dataset.id,
				node: sourcePackage
			}

			/**
			 * deliver event, emitted when package was succesfully delievered
			 *
			 * @event Porter#deliver
			 * @property {DOMNode} sourcePackage.node - The DOMNode of the package being dragged
			 * @property {DOMNode} destination.node - The DOMNode destination where the package is delivered to
			 * @property {DOMNode} source.node - The DOMNode destination where the pacakge came from if any
			 */
			porter.emit('deliver', args)
		} else if (result == Dnd.Delivery.COPY) {
			// Make a copy of the package DOM node in the new destination
			let copy = sourcePackage.cloneNode(true)
			if (porter.options.appendOnAcceptance) {
				destination.appendChild(copy)
			}

			args.deliveredPackage = {
				id: copy.dataset.id,
				node: copy
			}

			porter.emit('deliver', args)
		} else if (result == Dnd.Delivery.REJECT) {
			/**
			 * reject event, emitted when package was rejected from delievery
			 *
			 * @event Porter#reject
			 * @property {DOMNode} sourcePackage.node - The DOMNode of the package being dragged
			 * @property {DOMNode} destination.node - The DOMNode destination where the package is delivered to
			 * @property {DOMNode} source.node - The DOMNode destination where the pacakge came from if any
			 */
			porter.emit('reject', args)
		} else {
			throw new Error(`invalid response from options.accept: ${result}`)
		}
	}

	/**
	 * end event, emitted when drag has finished (regardless of delivery outcome)
	 *
	 * @event Porter#end
	 * @property {DOMNode} sourcePackage - The DOMNode of the package being dragged
	 * @property {DOMNode} destination - The DOMNode destination where the package was dropped on
	 * @property {String} result - the result from the accept method in the options if it was defined
	 */
	porter.emit('end', {sourcePackage, destination, result})

	if (options.refreshOnEnd) {
		porter.refresh()
	}
}


function px(val) {
	return val + 'px'
}

/**
 * Given a d3 event, returns the coordinates of the source event
 *
 * @param event
 */
function getEventCoords(event) {
	let eventType = event.sourceEvent.type
	let sourceEvent = eventType === 'touchstart' || eventType === 'touchmove' ?
		event.sourceEvent.touches[0] : event.sourceEvent
	return { x: sourceEvent.clientX, y: sourceEvent.clientY }
}

/**
 * Returns the destination that will receive the delivery based on current coords.
 */
function currentDestination (dndContainerNodes, delivery, event) {
	// Get the bounds for the delivery package and all of the destinations
	let deliveryBounds = _.pick(delivery.getBoundingClientRect(), 'top', 'left', 'height', 'width')

	let destinationBounds = Array.from(dndContainerNodes).map(n => {
		let bounds = _.pick(n.getBoundingClientRect(), 'top', 'left', 'height', 'width')
		bounds.node = n
		return bounds
	})

	// Magellan will tell use any overlapping of the delivery package
	// and the destinations
	let m = new Magellan(...(destinationBounds.concat([deliveryBounds])))
	let connections = m.explore(deliveryBounds)

	if (connections.length >= 2) {
		// First connection is the delivery pseudo node; any after that
		// are nodes that it overlaps.
		let overlappedBounds = connections.slice(1)

		// If the delivery node overlaps a single destination, use that
		if (overlappedBounds.length === 1) {
			return overlappedBounds[0].node
		}

		// If the delivery node overlaps multiple destinations, try to
		// use one that is closest to the mouse is hovering over
		let point = {
			x: event.sourceEvent.clientX,
			y: event.sourceEvent.clientY
		}

		//filter by nodes that contain the point, order by nodes closest to the point
		let boundsContainingMouse = overlappedBounds.filter(bounds => {
			return contains(bounds, point)
		}).sort((b1,b2) => {
			return closestToPoint(point, b1, b2)
		})

		if (boundsContainingMouse.length > 0) {
			//return the node that is closest to the point
			return boundsContainingMouse[0].node
		}

		// Otherwise, just use the first node returned by Magellan
		return overlappedBounds[0].node
	} else {
		return null
	}
}

/**
 * Returns true if the given bounds (top, left, height, width) contains the
 * given point (x,y)
 */
function contains (bounds, point) {
	let x1 = bounds.left
	let x2 = x1 + bounds.width
	let y1 = bounds.top
	let y2 = y1 + bounds.height

	return x1 <= point.x && x2 >= point.x
		&& y1 <= point.y && y2 >= point.y
}

/**
 * Returns -1 if bounds 1 is closest to the point, 0 if both bounds are
 * equidistant, and a 1 if bound 2 is closest to the point.  The closest
 * comparison looks at the distance from the point to the left and top side
 * of the bounds.  In the event that the bounds split the closest left/top side
 * the one with the smallest different is considered the closest
 *
 * @param point
 * @param bounds1
 * @param bounds2
 */
function closestToPoint(point, bounds1, bounds2) {
	//find the difference to the left side of the bounds
	let leftBound1 = point.x - bounds1.left
	let leftBound2 = point.x - bounds2.left
	let leftCompare = 0
	if (leftBound1 < leftBound2) {
		leftCompare = -1
	} else if (leftBound1 > leftBound2) {
		leftCompare = 1
	}

	//find the difference to the top side of the bounds
	let topBound1 = point.y - bounds1.top
	let topBound2 = point.y - bounds2.top
	let topCompare = 0
	if (topBound1 < topBound2) {
		topCompare = -1
	} else if (topBound1 > topBound2) {
		topCompare = 1
	}

	if (topCompare == leftCompare) {
		//if they agree, it doesn't matter which one so just use the left
		return leftCompare
	} else {
		let smallestleft = Math.min(leftBound1, leftBound2)
		let smallestTop = Math.min(topBound1, topBound2)
		//otherwise the smallest difference wins
		return smallestleft <= smallestTop ? leftCompare : topCompare
	}
}

function noDrag (domNode) {
	return d3.select(domNode).classed('no-drag')
}

export default Dnd;
